/**
 * Mage kit identity (plan 29 F9, lane BZ 2026-10-11).
 *
 * Before this lane the Mage auto and the Warlock auto shared the
 * `arcane_orbit` muzzle and fell to `hit_arcane`; Chain Lightning's impact
 * resolved through the map to `hit_lightning` and its bolt barely read;
 * Blizzard's hits were the shared `hit_ice`; Ice Barrier had a faint one-shot
 * cast and no state for its 8 s absorb; Fire Blast borrowed the shared
 * `fire_burst`; Frost Bolt's 800 ms cast showed a generic glow. Each def here
 * is named by the ability's TOML (`cast_effect` / `impact_effect`) so the
 * local module, the confirmed impact and a remote replay read the same id.
 *
 * Palette (TOML `aura_color`, ≥ 40 RGB apart inside the kit): auto pale
 * arcane blue, Frost Bolt glacier cyan, Blizzard snow white-blue, Ice Barrier
 * deep ice blue, Chain Lightning electric yellow, Fire Blast white-hot.
 * One-shot bursts end; the state is a persistent `status` def mapped in
 * STATUS_PRESENTATIONS. No screen shake or flash (the hit tier owns those).
 */
import type { VFXEffectConfig, VFXPrimitive } from '../../VFXEffectRegistry';
import { abilityPalette } from '../../abilityPalette';
import { COMBUSTION_STATE_STYLE as flame } from '../statusPresentation.config';
import { ringBlast, shardBurst, glintScatter, starFlash, wispRise } from './burstBuilders';

const WISP = 0x9fc4ff;
const WISP_CORE = 0xe8f1ff;
const GLACIER = 0x66d9ff;
const GLACIER_EDGE = 0xd8f6ff;
const SNOW = 0xd0ecff;
const ICE_DEEP = 0x3f8fd8;
const ICE_PALE = 0xbfe4ff;
const VOLT = 0xeaff4d;
const VOLT_CORE = 0xffffff;
const WHITE_HOT = 0xfff0b3;
const EMBER = 0xff7a1a;

/** Mage auto's hand spark: a small pale-blue four-point wisp released. */
const ARCANE_WISP_SPARK: VFXEffectConfig = {
  id: 'arcane_wisp_spark',
  name: 'Arcane Wisp Spark',
  category: 'burst',
  description: 'A small pale-blue four-point star flicks at the hand as the wisp leaves',
  primitives: [
    starFlash({ points: 4, innerRadius: 3, outerRadius: 11, color: WISP_CORE, spinRad: 0.8, expandTo: 1.6, durationMs: 170, glow: true }),
    glintScatter({ count: 3, spread: 9, color: WISP, durationMs: 160 }),
  ],
};

/** Mage auto's hit: a soft pale-blue pop, the wisp dispersing in motes. */
const ARCANE_WISP_POP: VFXEffectConfig = {
  id: 'arcane_wisp_pop',
  name: 'Arcane Wisp Pop',
  category: 'burst',
  description: 'A thin pale-blue ring pops on the target and four motes drift off — light, quick, arcane',
  primitives: [
    ringBlast({ radius: 9, color: WISP, alpha: 0.55, stroke: { color: WISP_CORE, width: 2 }, expandTo: 2.1, durationMs: 220 }),
    { type: 'circle', radius: 4, color: WISP_CORE, alpha: 0.95, depth: 97, glow: true,
      tween: { alpha: 0, scale: 1.8, duration: 140, ease: 'Power1' } },
    wispRise({ count: 4, spread: 12, radius: 2, color: WISP, travel: 16, durationMs: 300, glow: true }),
  ],
};

/** Frost Bolt's 800 ms cast: a ring of frost motes and an ice diamond growing over
 *  the mage's head (drawn over the sprite; symmetric — a wind-up spawn carries
 *  no facing). */
const FROST_BOLT_GATHER: VFXEffectConfig = {
  id: 'frost_bolt_gather',
  name: 'Frost Bolt Gather',
  category: 'burst',
  description: 'A ring of glacier-cyan motes brightens while an ice diamond grows bright over the mage\'s head',
  primitives: [
    { type: 'arc', arcRadius: 18, arcStartAngle: -180, arcEndAngle: 180, width: 2, offsetY: -50, color: GLACIER_EDGE, alpha: 0.85, depth: 101,
      tween: { scale: 0.6, duration: 760 } },
    { type: 'polygon', polygonPoints: [0, -9, 5, 0, 0, 9, -5, 0], offsetY: -50, color: GLACIER_EDGE, alpha: 0.85, depth: 102, glow: true,
      stroke: { color: GLACIER, width: 1.5 },
      tween: { scale: 1.8, alpha: 1, duration: 760 } },
    { type: 'circle', radius: 2.5, color: GLACIER_EDGE, alpha: 0.9, depth: 102, glow: true,
      scatter: { count: 6, radius: 56 },
      tween: { scale: 0.7, alpha: 1, duration: 760 } },
  ],
};

/** Blizzard's hit: hail — three ice shards drive down onto the target. */
const BLIZZARD_HAIL: VFXEffectConfig = {
  id: 'blizzard_hail',
  name: 'Blizzard Hail',
  category: 'burst',
  description: 'Three white-blue ice shards drive down onto the target and burst into a frost puff',
  primitives: [
    ...[-10, 0, 10].map((dx, i) => ({
      type: 'polygon' as const, polygonPoints: [0, -10, 3, 4, 0, 8, -3, 4],
      offsetX: dx, offsetY: -26, color: SNOW, alpha: 0.95, depth: 97,
      stroke: { color: ICE_DEEP, width: 1 },
      tween: { y: 24, alpha: 0, duration: 200, delay: i * 40, ease: 'Quad.easeIn' },
    })),
    ringBlast({ radius: 10, color: SNOW, alpha: 0.5, expandTo: 2, durationMs: 240, delayMs: 140 }),
    glintScatter({ count: 4, spread: 12, color: 0xffffff, durationMs: 200, delayMs: 160 }),
  ],
};

/** Chain Lightning's hand charge: a crackle of electric yellow. */
const CHAIN_LIGHTNING_CHARGE: VFXEffectConfig = {
  id: 'chain_lightning_charge',
  name: 'Chain Lightning Charge',
  category: 'burst',
  description: 'Electric-yellow forks crackle out from the hand around a white core as the bolt is thrown',
  primitives: [
    { type: 'circle', radius: 6, color: VOLT_CORE, alpha: 1, depth: 98, glow: true,
      tween: { alpha: 0, scale: 2.4, duration: 180, ease: 'Power2' } },
    { type: 'line', linePoints: [{ x: 0, y: 0 }, { x: 10, y: -8 }, { x: 16, y: -4 }, { x: 24, y: -12 }],
      lineWidth: 2, lineJitter: 2, color: VOLT, alpha: 1, depth: 97, glow: true,
      tween: { alpha: 0, duration: 160, ease: 'Power1' } },
    { type: 'line', linePoints: [{ x: 0, y: 0 }, { x: 9, y: 7 }, { x: 15, y: 4 }, { x: 22, y: 13 }],
      lineWidth: 2, lineJitter: 2, color: VOLT, alpha: 1, depth: 97, glow: true,
      tween: { alpha: 0, duration: 160, delay: 30, ease: 'Power1' } },
    glintScatter({ count: 4, spread: 14, color: VOLT, durationMs: 180 }),
  ],
};

/** Chain Lightning's hit, on each body it jumps to: a forked strike. */
const CHAIN_LIGHTNING_STRIKE: VFXEffectConfig = {
  id: 'chain_lightning_strike',
  name: 'Chain Lightning Strike',
  category: 'burst',
  description: 'A jagged electric-yellow fork cracks down onto the target with a white flash and a spark ring',
  primitives: [
    // The bolt: a white-hot column under a jagged yellow fork, held a beat
    // so it reads at play speed (a 3 px fork vanished under the hit flash).
    { type: 'rect', width: 5, height: 56, offsetY: -24, color: VOLT_CORE, alpha: 0.9, depth: 98, glow: true,
      tween: { alpha: 0, scaleX: 2.2, duration: 260, ease: 'Quad.easeOut' } },
    { type: 'line', linePoints: [{ x: -6, y: -56 }, { x: 6, y: -38 }, { x: -5, y: -22 }, { x: 5, y: -8 }, { x: 0, y: 6 }],
      lineWidth: 5, lineJitter: 3, color: VOLT, alpha: 1, depth: 99, glow: true,
      tween: { alpha: 0, duration: 320, ease: 'Quad.easeIn' } },
    { type: 'line', linePoints: [{ x: 6, y: -38 }, { x: 20, y: -30 }, { x: 26, y: -16 }],
      lineWidth: 3, color: VOLT, alpha: 0.95, depth: 99, glow: true,
      tween: { alpha: 0, duration: 280, delay: 20, ease: 'Quad.easeIn' } },
    { type: 'line', linePoints: [{ x: -5, y: -22 }, { x: -19, y: -16 }, { x: -24, y: -4 }],
      lineWidth: 3, color: VOLT, alpha: 0.95, depth: 99, glow: true,
      tween: { alpha: 0, duration: 280, delay: 40, ease: 'Quad.easeIn' } },
    { type: 'circle', radius: 8, color: VOLT_CORE, alpha: 1, depth: 97, glow: true,
      tween: { alpha: 0, scale: 2, duration: 150, ease: 'Power2' } },
    ringBlast({ radius: 12, color: VOLT, alpha: 0.5, stroke: { color: VOLT, width: 2 }, expandTo: 2.2, durationMs: 260 }),
    shardBurst({ count: 6, spread: 14, color: VOLT, shard: { width: 6, height: 1.5 }, travel: 30, durationMs: 220, glow: true }),
  ],
};

/** Ice Barrier's cast: six ice plates snap in around the body. */
const ICE_BARRIER_FORM: VFXEffectConfig = {
  id: 'ice_barrier_form',
  name: 'Ice Barrier Form',
  category: 'burst',
  description: 'Six deep-blue ice plates fly in and lock around the mage, a frost ring cracking outward',
  primitives: [
    { type: 'polygon', polygonPoints: [0, -9, 7, -4, 7, 5, 0, 9, -7, 5, -7, -4],
      color: ICE_PALE, alpha: 0.95, depth: 99, stroke: { color: ICE_DEEP, width: 2 },
      scatter: { count: 6, radius: 64 },
      tween: { scale: 0.6, alpha: 0, duration: 340, ease: 'Back.easeIn' } },
    ringBlast({ radius: 30, color: ICE_DEEP, alpha: 0.4, stroke: { color: ICE_PALE, width: 3 }, expandTo: 1.9, durationMs: 380, delayMs: 260 }),
    glintScatter({ count: 6, spread: 34, color: 0xffffff, durationMs: 240, delayMs: 280 }),
  ],
};

/** Ice Barrier's state: a faceted shell of ice for the absorb's lifetime. */
const ICE_BARRIER_STATE: VFXEffectConfig = {
  id: 'ice_barrier_state',
  name: 'Ice Barrier State',
  category: 'status',
  description: 'A faceted deep-blue ice shell around the body with a pale rim glinting: damage is soaked while it holds',
  followTarget: 'player',
  primitives: [
    { type: 'polygon', polygonPoints: [0, -54, 42, -30, 46, 22, 0, 54, -46, 22, -42, -30],
      color: ICE_DEEP, alpha: 0.2, depth: 99, stroke: { color: ICE_PALE, width: 2.5 },
      tween: { alpha: 0.32, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    { type: 'polygon', polygonPoints: [0, -8, 4, 0, 0, 8, -4, 0], offsetX: 30, offsetY: -28,
      color: 0xffffff, alpha: 0.2, depth: 101, glow: true,
      tween: { alpha: 0.95, duration: 700, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
  ],
};

/** Fire Blast's hand flick: a white-hot spark snapped forward. */
const FIRE_BLAST_FLICK: VFXEffectConfig = {
  id: 'fire_blast_flick',
  name: 'Fire Blast Flick',
  category: 'burst',
  description: 'A white-hot spark snaps off the hand with an ember lick behind it',
  primitives: [
    { type: 'circle', radius: 5, color: WHITE_HOT, alpha: 1, depth: 98, glow: true,
      tween: { alpha: 0, scale: 2.2, duration: 150, ease: 'Power2' } },
    { type: 'polygon', polygonPoints: [0, -4, 16, 0, 0, 4], color: EMBER, alpha: 0.85, depth: 97,
      tween: { alpha: 0, scaleX: 1.8, duration: 160, ease: 'Power1' } },
  ],
};

/** Fire Blast's hit: a sharp white-hot pop with ember petals. */
const FIRE_BLAST_BURST: VFXEffectConfig = {
  id: 'fire_blast_burst',
  name: 'Fire Blast Burst',
  category: 'burst',
  description: 'A white-hot core pops on the target and six ember petals flick outward — fast, not a fireball bloom',
  primitives: [
    { type: 'circle', radius: 9, color: WHITE_HOT, alpha: 1, depth: 98, glow: true,
      tween: { alpha: 0, scale: 2.2, duration: 170, ease: 'Power2' } },
    shardBurst({ count: 6, spread: 10, color: EMBER, shard: { points: [0, -3, 9, 0, 0, 3] }, travel: 30, durationMs: 230, glow: true }),
    ringBlast({ radius: 11, color: WHITE_HOT, alpha: 0.45, expandTo: 2, durationMs: 200, delayMs: 40 }),
  ],
};

const combustionPalette = abilityPalette('combustion')?.palette ?? [];

/** Held hand flames follow the status owner; no timer, particles or screen feel. */
const COMBUSTION_STATE: VFXEffectConfig = {
  id: 'combustion_state',
  name: 'Combustion State',
  category: 'status',
  minimumShapes: 2,
  description: 'Two orange hand flames with bright inner embers hold while spell power is raised',
  followTarget: 'player',
  // Shared Graphics budgets admit primitives in order. Preserve both outer
  // silhouettes with two free slots before spending slots on either ornament.
  // With fewer than two slots the existing cap still wins; no budget bypass.
  primitives: [
    ...[-1, 1].map((side): VFXPrimitive => ({
      type: 'polygon', polygonPoints: flame.flamePoints.map((n, i) => i % 2 === 0 ? n * side : n),
      offsetX: side * flame.handX, offsetY: flame.handY,
      color: combustionPalette[0], alpha: flame.flameAlpha, depth: flame.depth,
    })),
    ...[-1, 1].map((side): VFXPrimitive => ({
      type: 'line', linePoints: [...flame.flamePoints, ...flame.flamePoints.slice(0, 2)]
        .reduce<Array<{ x: number; y: number }>>((points, n, i, coords) => {
          if (i % 2 === 0) points.push({ x: n * side, y: coords[i + 1] });
          return points;
        }, []),
      offsetX: side * flame.handX, offsetY: flame.handY,
      lineWidth: flame.outlineWidth, color: combustionPalette[1], alpha: flame.coreAlpha, depth: flame.depth + 1,
    })),
    ...[-1, 1].map((side): VFXPrimitive => ({
      type: 'polygon', polygonPoints: flame.corePoints,
      offsetX: side * flame.handX, offsetY: flame.handY,
      color: combustionPalette[2], alpha: flame.coreAlpha, depth: flame.depth + 1,
    })),
  ],
};

export const MAGE_KIT_VFX: VFXEffectConfig[] = [
  ARCANE_WISP_SPARK, ARCANE_WISP_POP,
  FROST_BOLT_GATHER,
  BLIZZARD_HAIL,
  CHAIN_LIGHTNING_CHARGE, CHAIN_LIGHTNING_STRIKE,
  ICE_BARRIER_FORM, ICE_BARRIER_STATE,
  FIRE_BLAST_FLICK, FIRE_BLAST_BURST,
  COMBUSTION_STATE,
];
